import { css, html, LitElement, PropertyValues } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

import {
    Graph,
    GraphView,
    SeriesOptions,
    YAxisOptions,
} from '../../lib/components/graph';
import {
    BasicInterval,
    parseUTCInterval,
    Site,
    Timezone,
    UnitSystem,
} from '../../lib/common';
import { DATA_SERVICE$ } from '../../environment';
import { DataService } from '../../lib/data';

import '../../lib/components/graph';
import {DateTime, DurationLike, DurationUnit, Interval} from 'luxon';
import {GraphSpec} from "./graph-spec";
import {TransmitterType, TransmitterTypesList} from "../../lib/common/transmitter";

const STORAGE_KEY = 'USCRN_SENSOR_GRAPH';

function storeSetting(key: string, value: string) {
    localStorage.setItem(`${STORAGE_KEY}:${key}`, value);
}

function getSetting(key: string, fallback?: string): string {
    return localStorage.getItem(`${STORAGE_KEY}:${key}`) || fallback;
}

const UNIT_SYSTEM_KEY = 'unitSystem';
const METRIC = 'metric';
const US = 'US;';


@customElement('uscrn-sensor-graph')
export class SensorGraph extends LitElement {
    public static get styles() {
        return css`
            :host {
                display: flex;
                flex-direction: column;
            }

            #graph {
                flex-grow: 1;
            }


            #graph-controls {
                padding: 8px;
                display: flex;
                flex-direction: row;
                overflow: auto;
            }

            @media screen and (min-width: 1000px){
              #graph-controls {
                justify-content: center;
              }
            }

            #graph-controls > button,
            label,
            select {
                margin: 0 8px;
            }

            #graph-controls > button {
                width: 75px;
            }

            #site-selection {
                width: 175px;
            }

            .vertical-rule {
                width: 0px;
                height: 100%;
                border-right: 1px solid #aaa;
            }
        `;
    }

    private dataPromise: Promise<DataService> = DATA_SERVICE$.then((data) => {
        this.loading = false;
        return (this.data = data);
    });

    @state() private data: DataService;
    @state() private loading = true;
    @state() private unitSystem: UnitSystem =
        getSetting(UNIT_SYSTEM_KEY, METRIC) === METRIC
            ? UnitSystem.METRIC
            : UnitSystem.US;

    @property() public config: GraphSpec;
    @property() public siteId: number;
    @property() public transmitter: TransmitterType = 'default';
    @property() public zone: Timezone;
    @property() public displayRange: Interval | BasicInterval;

    private availableSnapUnits: { [K in DurationUnit]?: string } = {
        hours: 'Hour',
        days: 'Day',
        weeks: 'Week',
        months: 'Month',
    };

    private selectedSnapUnit: keyof SensorGraph['availableSnapUnits'] = 'days';

    protected updated(_changedProperties: PropertyValues) {
        if (_changedProperties.has('unitSystem')) {
            storeSetting(
                UNIT_SYSTEM_KEY,
                this.unitSystem === UnitSystem.METRIC ? METRIC : US
            );
        }
        if (_changedProperties.has('siteId')) {
            this.dataPromise.then((data) => {
                this.dispatchEvent(
                    new CustomEvent('siteChange', {
                        detail: data.getSite(this.siteId),
                    })
                );
            });
        }
    }

    protected render(): unknown {
        return html`
            <div id="graph">
                <uscrn-graph
                    .data=${this.data}
                    .loading=${this.loading}
                    .unitSystem=${this.unitSystem}
                    .seriesOptions=${this.config?.series}
                    .yAxisOptions=${this.config?.yAxes}
                    .displayTimezone=${this.zone}
                    .displayRange=${this.displayRange}
                    .siteId=${this.siteId}
                    .transmitter=${this.transmitter}
                    @view-update=${(ev: CustomEvent<GraphView>) => {
                        this.displayRange = ev.detail.xRange;
                    }}
                >
                </uscrn-graph>
            </div>
            <div id="graph-controls">
                <label for="timezone">Timezone</label>
                <select
                    id="timezone"
                    @change=${(ev) => (this.zone = ev.target.value)}
                >
                    <option ?selected=${this.zone === 'UTC'} value="UTC">
                        UTC
                    </option>
                    <option ?selected=${this.zone === 'LST'} value="LST">
                        LST
                    </option>
                </select>

                <div class="vertical-rule"></div>

                <label for="us-units">Unit System</label>
                <select
                    id="us-units"
                    @change=${(ev) =>
                        (this.unitSystem = ev.target.selectedOptions[0].system)}
                >
                    <option
                        ?selected=${this.unitSystem === UnitSystem.US}
                        .system=${UnitSystem.US}
                    >
                        US Customary
                    </option>
                    <option
                        ?selected=${this.unitSystem === UnitSystem.METRIC}
                        .system=${UnitSystem.METRIC}
                    >
                        Metric
                    </option>
                </select>

                <div class="vertical-rule"></div>

                <div>
                    <label for="site-selection">Site</label>
                    <select
                        id="site-selection"
                        @change=${(ev) =>
                            (this.siteId = parseInt(ev.target.value))}
                    >
                        ${this.data
                            ?.getAllSites()
                            .filter(s => s.publiclyVisible)
                            .map(
                                (site) => html`
                                    <option
                                        value="${site.id}"
                                        ?selected=${this.siteId === site.id}
                                    >
                                        ${site.displayName}
                                    </option>
                                `
                            )}
                    </select>
                </div>
                 ${
                    this.data?.getSite(this.siteId).stations.length > 1  ?
                        html`
                        <div>
                            <label for="transmitter-selection">Transmitter</label>
                            <select
                                    id="transmitter-selection"
                                    @change=${(ev) =>
                                            (this.transmitter = ev.target.value)}
                            >
            
                                ${TransmitterTypesList.map((transmitter) => html`
                                    <option
                                            value="${transmitter}"
                                            ?selected=${this.transmitter === transmitter}
                                    >
                                        ${transmitter}
                                    </option>
            
                                `)}
            
                            </select>
                        </div>
                    ` : html `<div/>`
                 }

                <div class="vertical-rule"></div>

                <label for="time-scale">Snap To</label>

                <select
                    id="time-scale"
                    @change=${(ev) => (this.selectedSnapUnit = ev.target.value)}
                >
                    ${Object.keys(this.availableSnapUnits).map(
                        (unit) => html`
                            <option
                                ?selected=${unit === this.selectedSnapUnit}
                                value="${unit}"
                            >
                                ${this.availableSnapUnits[unit]}
                            </option>
                        `
                    )}
                </select>

                <button @click=${() => this.snap(-1)}>Previous</button>
                <button @click=${() => this.snap(1)}>Next</button>
                <button @click=${() => this.current()}>Current</button>

                <div class="vertical-rule"></div>

                <button
                    id="download-image"
                    @click=${() => (this.shadowRoot.querySelector('uscrn-graph') as Graph).downloadPNGImage()}
                >
                    Download Image
                </button>
            </div>
        `;
    }

    /**
     * Sets the display range to the selected snap unit ('hour, 'day', etc) and shifts it forward or backward
     * by that amount.
     * Internally, creates a time interval with the length of the selected snap unit that has the same
     * center time as the current display range. This interval is then shifted forward or backward based on
     * the unitShift parameter.
     * @param unitShift how many snap units to shift forward or backward (i.e. -1 or 1)
     * @private
     */
    private snap(unitShift: number): void {
        const displayRange = parseUTCInterval(this.displayRange);

        const centerOfInterval = DateTime.fromMillis(
            (displayRange.start.toMillis() + displayRange.end.toMillis()) / 2
        );

        const durationObject: DurationLike = {};
        durationObject[this.selectedSnapUnit] = 0.5;

        let startOfInterval = centerOfInterval.minus(durationObject);
        let endOfInterval = centerOfInterval.plus(durationObject);

        durationObject[this.selectedSnapUnit] = unitShift;
        startOfInterval = startOfInterval.plus(durationObject);
        endOfInterval = endOfInterval.plus(durationObject);

        this.displayRange = startOfInterval.until(endOfInterval);
    }

    /**
     * Sets the display range to show the time range ending at the start of the current hour,
     * with the length of the selected snap unit ('hour', 'day', etc)
     * @private
     */
    private current(): void {
        const endOfInterval = DateTime.now().startOf('hour');

        const durationObject: DurationLike = {};
        durationObject[this.selectedSnapUnit] = 1;

        const startOfInterval = endOfInterval.minus(durationObject);

        this.displayRange = startOfInterval.until(endOfInterval);
    }
}
